import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, Button, Badge, Select } from '../components/ui';
import { api, ApiError } from '../lib/api';
import type { ApiResponse } from '../lib/types';

interface Token { id: number; client_id?: number; token: string; name?: string; status?: string; created_at?: string; last_used_at?: string | null }
interface ClientOpt { id: number; name: string }

const Code = ({ children }: { children: string }) => (
  <pre className="rounded-lg bg-[#2d2d2d] text-[#f8f8f2] p-4 text-xs overflow-x-auto whitespace-pre">{children}</pre>
);
const H3 = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-base font-semibold text-ink-900 border-b-2 border-border pb-2 mb-3 mt-8">{children}</h3>
);
const H4 = ({ children }: { children: React.ReactNode }) => <h4 className="text-sm font-semibold text-slate-700 mt-4 mb-1.5">{children}</h4>;
const Tbl = ({ head, rows }: { head: string[]; rows: (string | React.ReactNode)[][] }) => (
  <div className="overflow-x-auto my-2">
    <table className="text-xs border border-border rounded-lg overflow-hidden">
      <thead><tr className="bg-surface-muted text-left text-slate-500">{head.map((h) => <th key={h} className="px-3 py-2 font-medium border-b border-border">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-border">{rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="px-3 py-1.5 align-top">{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);
const Note = ({ items }: { items: string[] }) => (
  <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs px-4 py-3 my-3">
    <strong>Notes:</strong>
    <ul className="mt-1 space-y-0.5">{items.map((t, i) => <li key={i}>• {t}</li>)}</ul>
  </div>
);

export default function Docs() {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [clientId, setClientId] = useState('');
  const [copied, setCopied] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api.get<ApiResponse<ClientOpt[]>>('/clients/list').then((r) => setClients(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (clientId) loadTokens(clientId); else setTokens([]); }, [clientId]);

  function loadTokens(cid: string) {
    api.get<ApiResponse<Token[]>>(`/api-tokens/list?client_id=${cid}`).then((r) => setTokens(r.data || [])).catch(() => setTokens([]));
  }
  async function generate() {
    if (!clientId) { alert('Select a client first'); return; }
    const name = prompt('Token name (optional):', 'Default');
    if (name === null) return;
    try { await api.post('/api-tokens/create', { client_id: Number(clientId), name: name || 'Default' }); loadTokens(clientId); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Failed'); }
  }
  async function revoke(t: Token) {
    if (!confirm('Are you sure you want to revoke this token? This cannot be undone.')) return;
    try { await api.post('/api-tokens/revoke', { token_id: t.id, client_id: Number(clientId) }); loadTokens(clientId); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Failed'); }
  }
  function copy(t: Token) { navigator.clipboard.writeText(t.token).then(() => { setCopied(t.id); setTimeout(() => setCopied(null), 2000); }); }

  return (
    <Layout title="API Documentation">
      <Card className="p-6 mb-6">
        <div className="rounded-lg bg-sky-50 border border-sky-200 text-sky-900 text-sm px-4 py-3">
          ℹ This public API allows external systems to list campaigns and fetch daily reports (pageviews &amp; clicks). Every request requires a <strong>Bearer token</strong> tied to a client. Tokens are managed below.
        </div>

        <H3>🔒 Authentication</H3>
        <p className="text-sm text-slate-600 mb-2">Every request must include the <code className="bg-surface-muted px-1 rounded">Authorization</code> header with a Bearer token.</p>
        <Code>{'Authorization: Bearer <token>'}</Code>
        <p className="text-sm text-slate-600 my-2">The token is tied to a <strong>client</strong>. Responses are filtered automatically — you only see campaigns and reports from your own client.</p>
        <p className="text-sm text-slate-600 mb-2">If the token is invalid or revoked, the API returns <code>401</code>:</p>
        <Code>{'{ "error": "Invalid or revoked API token" }'}</Code>

        <H3>📋 1. List Campaigns</H3>
        <div className="flex items-center gap-2 mb-2"><Badge tone="green">GET</Badge><code className="text-sm">/api/v1/campaigns</code></div>
        <p className="text-sm text-slate-600">Returns all active campaigns for the authenticated client.</p>
        <H4>Request</H4>
        <Code>{`curl -H "Authorization: Bearer YOUR_TOKEN" \\
  https://<dashboard-host>/api/v1/campaigns`}</Code>
        <H4>Response 200</H4>
        <Code>{`{
  "success": true,
  "data": [
    { "campaignId": "a1b2c3d4-e5f6-7890-abcd-ef1234567890", "campaignName": "Campaign Black Friday 2026", "type": "retargeting" },
    { "campaignId": "f9e8d7c6-b5a4-3210-fedc-ba0987654321", "campaignName": "Campaign Christmas", "type": "branding" }
  ]
}`}</Code>
        <H4>Response Fields</H4>
        <Tbl head={['Field', 'Type', 'Description']} rows={[
          [<code>campaignId</code>, 'string (UUID)', 'Unique campaign identifier'],
          [<code>campaignName</code>, 'string', 'Campaign name'],
          [<code>type</code>, 'string', <>Campaign type: <code>retargeting</code> or <code>branding</code></>],
        ]} />
        <Note items={[
          'Campaigns with status deleted are not included in the listing.',
          'Results are sorted by creation date (most recent first).',
          'Use the campaignId to fetch the report for each campaign.',
        ]} />

        <H3>📊 2. Campaign Report</H3>
        <div className="flex items-center gap-2 mb-2"><Badge tone="green">GET</Badge><code className="text-sm">/api/v1/campaigns/:campaignId/report</code></div>
        <p className="text-sm text-slate-600">Returns the daily report with <strong>pageviews</strong> and <strong>clicks</strong> per day, plus the accumulated totals for the period.</p>
        <H4>URL Parameters</H4>
        <Tbl head={['Parameter', 'Type', 'Required', 'Description']} rows={[[<code>campaignId</code>, 'string (UUID)', 'Yes', 'Campaign ID (from the list endpoint)']]} />
        <H4>Query Parameters</H4>
        <Tbl head={['Parameter', 'Type', 'Default', 'Description']} rows={[[<code>dateFilter</code>, 'string', <code>currentmonth</code>, 'Date range filter (see options below)']]} />
        <H4>dateFilter Options</H4>
        <Tbl head={['Value', 'Description']} rows={[
          [<code>today</code>, 'Today only'], [<code>yesterday</code>, 'Yesterday only'],
          [<code>last7days</code>, 'Last 7 days (including today)'], [<code>currentmonth</code>, 'Current month (1st to last day)'],
          [<code>last30days</code>, 'Last 30 days'], [<code>last60days</code>, 'Last 60 days'], [<code>last90days</code>, 'Last 90 days'],
        ]} />
        <H4>Request</H4>
        <Code>{`curl -H "Authorization: Bearer YOUR_TOKEN" \\
  "https://<dashboard-host>/api/v1/campaigns/a1b2c3d4-e5f6-7890-abcd-ef1234567890/report?dateFilter=last30days"`}</Code>
        <H4>Response 200</H4>
        <Code>{`{
  "success": true,
  "data": {
    "campaignId": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "campaignName": "Campaign Black Friday 2026",
    "dateFilter": "last30days",
    "totals": { "pageviews": 45230, "clicks": 1287 },
    "report": [
      { "date": "2026-01-26", "pageviews": 1520, "clicks": 43 },
      { "date": "2026-01-27", "pageviews": 1480, "clicks": 38 }
    ]
  }
}`}</Code>
        <Note items={[
          'Days without data are not included in the report array (they are not filled with zeros).',
          'The totals field is the sum of every record in the report array.',
          'If the campaign does not belong to your client, it returns 404.',
        ]} />

        <H3>⚠ Error Responses</H3>
        <Tbl head={['HTTP Status', 'Body', 'Cause']} rows={[
          [<Badge tone="red">401</Badge>, <code>{'{ "error": "Missing API token..." }'}</code>, 'Missing Authorization header'],
          [<Badge tone="red">401</Badge>, <code>{'{ "error": "Invalid or revoked API token" }'}</code>, 'Invalid, expired or revoked token'],
          [<Badge tone="amber">404</Badge>, <code>{'{ "error": "Campaign not found" }'}</code>, 'Campaign does not exist or does not belong to your client'],
          [<Badge tone="neutral">500</Badge>, <code>{'{ "error": "Internal server error" }'}</code>, 'Internal error'],
        ]} />

        <H3>{'</> Integration Examples'}</H3>
        <H4>Recommended Flow</H4>
        <ol className="text-sm text-slate-600 list-decimal pl-5 space-y-1 mb-3">
          <li><strong>List the campaigns</strong> to discover the available IDs</li>
          <li><strong>For each campaign</strong>, fetch the report with the desired filter</li>
          <li><strong>Store locally</strong> to avoid repeated calls</li>
          <li><strong>Schedule updates</strong> — data is refreshed once a day</li>
        </ol>
        <H4>Node.js</H4>
        <Code>{`const API_URL = 'https://<dashboard-host>';
const API_TOKEN = 'your_token_here';
const headers = { 'Authorization': \`Bearer \${API_TOKEN}\` };

// 1. List campaigns
const campaignsRes = await fetch(\`\${API_URL}/api/v1/campaigns\`, { headers });
const campaigns = await campaignsRes.json();

// 2. Fetch report for each campaign
for (const campaign of campaigns.data) {
  const reportRes = await fetch(\`\${API_URL}/api/v1/campaigns/\${campaign.campaignId}/report?dateFilter=currentmonth\`, { headers });
  const report = await reportRes.json();
  console.log(\`\${campaign.campaignName}:\`);
  console.log(\`  Total pageviews: \${report.data.totals.pageviews}\`);
  console.log(\`  Total clicks: \${report.data.totals.clicks}\`);
}`}</Code>
        <H4>Python</H4>
        <Code>{`import requests

API_URL = 'https://<dashboard-host>'
API_TOKEN = 'your_token_here'
headers = {'Authorization': f'Bearer {API_TOKEN}'}

# 1. List campaigns
campaigns = requests.get(f'{API_URL}/api/v1/campaigns', headers=headers).json()

# 2. Report for each campaign
for campaign in campaigns['data']:
    report = requests.get(f'{API_URL}/api/v1/campaigns/{campaign["campaignId"]}/report',
                          params={'dateFilter': 'currentmonth'}, headers=headers).json()
    print(f'{campaign["campaignName"]}:')
    print(f'  Pageviews: {report["data"]["totals"]["pageviews"]}')
    print(f'  Clicks: {report["data"]["totals"]["clicks"]}')`}</Code>
        <H4>cURL</H4>
        <Code>{`# List campaigns
curl -s -H "Authorization: Bearer YOUR_TOKEN" \\
  https://<dashboard-host>/api/v1/campaigns | jq .

# Campaign report (current month)
curl -s -H "Authorization: Bearer YOUR_TOKEN" \\
  "https://<dashboard-host>/api/v1/campaigns/CAMPAIGN_ID/report?dateFilter=currentmonth" | jq .`}</Code>
      </Card>

      {/* Token management */}
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
          <span className="text-sm font-semibold text-ink-900">API Tokens</span>
          <div className="flex gap-2 items-center">
            <Select value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-56">
              <option value="">Select a client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={generate} disabled={!clientId}>+ Generate New Token</Button>
          </div>
        </div>
        {error && <div className="px-4 py-2 text-sm text-rose-700 bg-rose-50">{error}</div>}
        <div className="p-4">
          <div className="rounded-lg bg-sky-50 border border-sky-200 text-sky-900 text-xs px-4 py-3 mb-3">
            <strong>Public API Endpoints:</strong>
            <div><code>GET /api/v1/campaigns</code> — list campaigns</div>
            <div><code>GET /api/v1/campaigns/:id/report?dateFilter=currentmonth</code> — report</div>
            <div className="mt-1">Header: <code>Authorization: Bearer &lt;token&gt;</code></div>
          </div>
          {!clientId ? <p className="text-sm text-slate-400">Select a client to view its tokens.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-slate-500 bg-surface-muted"><th className="px-3 py-2 font-medium">Name</th><th className="px-3 py-2 font-medium">Token</th><th className="px-3 py-2 font-medium">Status</th><th className="px-3 py-2 font-medium">Created</th><th className="px-3 py-2 font-medium">Last Used</th><th className="px-3 py-2 font-medium text-right">Actions</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {tokens.map((t) => (
                    <tr key={t.id}>
                      <td className="px-3 py-2">{t.name || 'Default'}</td>
                      <td className="px-3 py-2"><code className="text-[11px] break-all">{t.token}</code></td>
                      <td className="px-3 py-2">{t.status === 'active' ? <Badge tone="green">Active</Badge> : <Badge tone="neutral">Revoked</Badge>}</td>
                      <td className="px-3 py-2 text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-3 py-2 text-slate-500">{t.last_used_at ? new Date(t.last_used_at).toLocaleString() : 'Never'}</td>
                      <td className="px-3 py-2">
                        {t.status === 'active' && (
                          <div className="flex justify-end gap-1.5">
                            <Button variant="outline" onClick={() => copy(t)}>{copied === t.id ? '✓ Copied!' : '⧉ Copy'}</Button>
                            <Button variant="danger" onClick={() => revoke(t)}>✕ Revoke</Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {tokens.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No API tokens yet. Click "Generate New Token" to create one.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </Layout>
  );
}
